import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { selectCurrentUser } from '@shared/store/auth.duck';
import {
  joinWaitingList,
  leaveWaitingList,
  selectIsLockersLoading,
  selectIsLockersSaving,
  selectLockerSummary,
  selectLockers,
  selectLockerWaitingList,
  unassignLocker
} from '../data/lockers.duck';
import { Locker, LockerOfficeSummary, WaitingListEntry } from '../data/lockers.models';
import { formatDate, ordinal, rowFromLabel } from '../data/lockers.utils';
import {
  EmptyState,
  HeroAction,
  HeroCardBody,
  HeroCardDetails,
  HeroRow,
  HeroTileValue,
  HeroValue,
  IconTile,
  LockerCard,
  LockerCardHeader,
  LockerPageBody,
  NarrowSubtitle,
  ProgressFill,
  ProgressTrack,
  RowAction,
  TableCell,
  TableRow,
  WideBlockCell,
  WideCell
} from './LockerViewComponents';

const AVAILABILITY_COLUMNS = 'minmax(0, 1fr) minmax(0, 130px) minmax(0, 220px) minmax(0, 150px)';
const PERSONAL_BORDER = 'rgba(0, 87, 255, 0.25)';
const PERSONAL_TILE = 'rgba(0, 87, 255, 0.08)';
const WAITING_BORDER = 'rgba(240, 130, 34, 0.4)';
const WAITING_TILE = 'rgba(240, 130, 34, 0.1)';

/**
 * Designs 2a and 2b. A member gets two questions answered and nothing else: which locker is mine,
 * and is anything free anywhere. Everything on this page comes from the summary endpoint plus the
 * member's own locker, so no assignee name can reach it.
 */
export function MemberLockerView(props: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentUser = useSelector(selectCurrentUser);
  const lockers = useSelector(selectLockers);
  const summary = useSelector(selectLockerSummary);
  const waitingList = useSelector(selectLockerWaitingList);
  const isLoading = useSelector(selectIsLockersLoading);
  const isSaving = useSelector(selectIsLockersSaving);

  // The backend reveals the assignee only when it is the caller, which is what makes this findable.
  const myLocker = useMemo(() => {
    return lockers.find((locker: Locker) => !!locker.assignedUserId && locker.assignedUserId === currentUser?.id);
  }, [lockers, currentUser]);

  const myEntry = useMemo(() => {
    return (waitingList?.entries ?? []).find((entry: WaitingListEntry) => entry.userId === currentUser?.id);
  }, [waitingList, currentUser]);

  const myPosition = waitingList?.myPosition ?? null;
  const totalWaiting = waitingList?.totalWaiting ?? 0;

  /** The office worth pointing someone at: free lockers first, shortest queue after that. */
  const suggestedOffice = useMemo(() => {
    return summary
      .filter((office: LockerOfficeSummary) => office.officeId !== props.selectedOfficeId)
      .filter((office: LockerOfficeSummary) => office.availableLockers > 0)
      .sort((a: LockerOfficeSummary, b: LockerOfficeSummary) =>
        (b.availableLockers - a.availableLockers) || (a.waitingListCount - b.waitingListCount))[0];
  }, [summary, props.selectedOfficeId]);

  const onGiveUpLocker = () => {
    if (!myLocker) return;
    if (!window.confirm(t('lockers.confirm-give-up', { label: myLocker.label }))) return;
    dispatch(unassignLocker(myLocker.id));
  };

  const lockerLocation = useMemo(() => {
    if (!myLocker) return '';
    const row = rowFromLabel(myLocker.label);
    const place = [myLocker.officeName, row ? t('lockers.row-label', { row }) : null].filter(Boolean).join(', ');
    const since = myLocker.assignedAt ? t('lockers.yours-since', { date: formatDate(myLocker.assignedAt) }) : '';
    return [place, since].filter(Boolean).join(' · ');
  }, [myLocker, t]);

  if (isLoading && lockers.length === 0) {
    return <LockerPageBody><SimpleSpinner size={40} /></LockerPageBody>;
  }

  return (
    <LockerPageBody>
      <div>
        <HeadlineMedium style={{ marginBottom: 4 }}>{t('lockers.title')}</HeadlineMedium>
        <BodySmall colour={Colours.darkGrey}>
          {myLocker
            ? t('lockers.member-subtitle-holder')
            : t('lockers.member-subtitle-no-locker', { office: props.selectedOfficeName })}
        </BodySmall>
      </div>

      <HeroRow>
        {myLocker && (
          <>
            <LockerCard accent={PERSONAL_BORDER} style={{ flex: 1 }}>
              <HeroCardBody>
                <IconTile tileSize={72} backgroundColour={PERSONAL_TILE}>
                  <Icon icon={IconTypes.Padlock} style={{ color: Colours.blue, fontSize: 32 }} />
                </IconTile>

                <HeroCardDetails>
                  <BodySmall weight={600} colour={Colours.darkGrey}>{t('lockers.your-locker')}</BodySmall>
                  <HeroValue>{myLocker.label}</HeroValue>
                  <BodyRegular colour={Colours.darkGrey}>{lockerLocation}</BodyRegular>
                </HeroCardDetails>

                <HeroAction>
                  <OutlineButton text={'lockers.give-up-locker'} disabled={isSaving} click={onGiveUpLocker} />
                </HeroAction>
              </HeroCardBody>
            </LockerCard>

            <LockerCard style={{ flexShrink: 0 }}>
              <HeroCardBody style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                <BodySmall weight={600} colour={Colours.darkGrey}>{t('lockers.waiting-list')}</BodySmall>
                <BodyRegular>{t('lockers.not-waiting-you-have-one')}</BodyRegular>
                <BodySmall colour={Colours.darkGrey}>
                  {t('lockers.people-waiting-at', { count: totalWaiting, office: props.selectedOfficeName })}
                </BodySmall>
              </HeroCardBody>
            </LockerCard>
          </>
        )}

        {!myLocker && myPosition != null && (
          <>
            <LockerCard accent={WAITING_BORDER} style={{ flex: 1 }}>
              <HeroCardBody>
                <IconTile tileSize={72} backgroundColour={WAITING_TILE}>
                  <HeroTileValue colour={Colours.orange}>{myPosition}</HeroTileValue>
                </IconTile>

                <HeroCardDetails>
                  <BodySmall weight={600} colour={Colours.darkGrey}>{t('lockers.your-position')}</BodySmall>
                  <HeroValue>
                    {t('lockers.position-in-queue', { position: ordinal(myPosition), office: props.selectedOfficeName })}
                  </HeroValue>
                  <BodyRegular colour={Colours.darkGrey}>
                    {[
                      myEntry ? t('lockers.you-joined-on', { date: formatDate(myEntry.createdAt) }) : null,
                      t('lockers.waiting-in-total', { count: totalWaiting }),
                    ].filter(Boolean).join(' · ')}
                  </BodyRegular>
                </HeroCardDetails>

                <HeroAction>
                  <OutlineButton text={'lockers.leave-waiting-list'} disabled={isSaving || !myEntry}
                                 click={() => myEntry && dispatch(leaveWaitingList(myEntry.id))} />
                </HeroAction>
              </HeroCardBody>
            </LockerCard>

            {!!suggestedOffice && (
              <LockerCard style={{ flexShrink: 0 }}>
                <HeroCardBody style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                  <BodySmall weight={600} colour={Colours.darkGrey}>{t('lockers.another-office')}</BodySmall>
                  <BodyRegular>
                    {t('lockers.office-has-free', {
                      office: suggestedOffice.officeName,
                      count: suggestedOffice.availableLockers,
                    })}
                  </BodyRegular>
                  <HeroAction>
                    <PrimaryButton text={t('lockers.join-office-list', { office: suggestedOffice.officeName })}
                                   disabled={isSaving}
                                   click={() => dispatch(joinWaitingList({ officeId: suggestedOffice.officeId }))} />
                  </HeroAction>
                </HeroCardBody>
              </LockerCard>
            )}
          </>
        )}

        {!myLocker && myPosition == null && (
          <LockerCard style={{ flex: 1 }}>
            <HeroCardBody>
              <IconTile tileSize={72} backgroundColour={PERSONAL_TILE}>
                <Icon icon={IconTypes.Padlock} style={{ color: Colours.blue, fontSize: 32 }} />
              </IconTile>

              <HeroCardDetails>
                <HeroValue>{t('lockers.no-locker-here', { office: props.selectedOfficeName })}</HeroValue>
                <BodyRegular colour={Colours.darkGrey}>
                  {t('lockers.people-waiting-at', { count: totalWaiting, office: props.selectedOfficeName })}
                </BodyRegular>
              </HeroCardDetails>

              <HeroAction>
                <PrimaryButton text={'lockers.join-waiting-list'} disabled={isSaving || !props.selectedOfficeId}
                               click={() => props.selectedOfficeId
                                 && dispatch(joinWaitingList({ officeId: props.selectedOfficeId }))} />
              </HeroAction>
            </HeroCardBody>
          </LockerCard>
        )}
      </HeroRow>

      <LockerCard>
        <LockerCardHeader>
          <BodyLarge weight={600}>{t('lockers.availability-by-office')}</BodyLarge>
          <BodySmall colour={Colours.darkGrey}>
            {myLocker ? t('lockers.numbers-only-note') : t('lockers.join-from-any-office')}
          </BodySmall>
        </LockerCardHeader>

        {summary.length === 0 && (
          <EmptyState>
            <BodyRegular colour={Colours.darkGrey}>{t('lockers.no-lockers-yet')}</BodyRegular>
          </EmptyState>
        )}

        {summary.map((office: LockerOfficeSummary) => {
          const isMyOffice = office.officeId === props.selectedOfficeId;
          const occupancy = office.totalLockers === 0 ? 0 : (office.assignedLockers / office.totalLockers) * 100;

          return (
            <TableRow key={office.officeId} columns={AVAILABILITY_COLUMNS}>
              <TableCell>
                <Icon icon={IconTypes.Office} style={{ color: Colours.mildGrey }} />
                <div style={{ minWidth: 0 }}>
                  <BodyRegular weight={600}>{office.officeName}</BodyRegular>
                  <NarrowSubtitle>
                    <BodySmall colour={Colours.darkGrey}>
                      {[
                        office.availableLockers > 0
                          ? t('lockers.free-count', { count: office.availableLockers })
                          : t('lockers.none-free'),
                        isMyOffice ? t('lockers.your-office') : null,
                      ].filter(Boolean).join(' · ')}
                    </BodySmall>
                  </NarrowSubtitle>
                </div>
              </TableCell>

              <WideCell>
                <BodyRegular weight={600}
                             colour={office.availableLockers > 0 ? Colours.darkGreen : Colours.darkGrey}>
                  {office.availableLockers > 0
                    ? t('lockers.free-count', { count: office.availableLockers })
                    : t('lockers.none-free')}
                </BodyRegular>
              </WideCell>

              <WideBlockCell>
                <ProgressTrack height={8}>
                  <ProgressFill percent={occupancy} />
                </ProgressTrack>
                <BodySmall colour={Colours.darkGrey}>{t('lockers.of-total', { count: office.totalLockers })}</BodySmall>
              </WideBlockCell>

              {myLocker || isMyOffice
                ? <WideCell style={{ justifyContent: 'flex-end' }}>
                    <BodySmall colour={Colours.darkGrey}>
                      {isMyOffice
                        ? t('lockers.your-office')
                        : (office.waitingListCount > 0
                          ? t('lockers.waiting-count', { count: office.waitingListCount })
                          : t('lockers.no-queue'))}
                    </BodySmall>
                  </WideCell>
                : <RowAction width={120}>
                    <OutlineButton text={'lockers.join-list'} size={ButtonSize.Small} disabled={isSaving}
                                   click={() => dispatch(joinWaitingList({ officeId: office.officeId }))} />
                  </RowAction>}
            </TableRow>
          );
        })}
      </LockerCard>
    </LockerPageBody>
  );
}

interface Props {
  selectedOfficeId: number | null;
  selectedOfficeName: string;
}
